import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useReport } from "../../../contexts/ReportContext";
import { useSupervisor } from "../../../contexts/SupervisorContext";
import { useCase } from "../../../contexts/CaseContext";
import { useAccount } from "../../../contexts/AccountContext";
import moment from "moment";

export const useReportDetail = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { isFL324PExportEnabled } = useAccount();
  const { cases } = useCase();
  const { supervisors } = useSupervisor();
  const { selectedReport, setSelectedReport, loading, error } = useReport();
  const [createFL324PDialogOpen, setCreateFL324PDialogOpen] = useState(false);

  // Memoized supervisor lookup
  const supervisor = useMemo(() => {
    if (!selectedReport?.supervisorId || !supervisors.length) return null;
    return supervisors.find(
      (supervisor) => supervisor.id === selectedReport.supervisorId,
    ) ?? null;
  }, [selectedReport?.supervisorId, supervisors]);

  // Memoized case lookup
  const selectedCase = useMemo(() => {
    if (!selectedReport?.caseId || !cases.length) return null;
    return cases.find((c) => c.id === selectedReport.caseId) ?? null;
  }, [selectedReport?.caseId, cases]);

  // Memoized visit date formatting
  const visitDate = useMemo(() => {
    if (!selectedReport?.visit?.startDate) return "No visit date";
    return moment(selectedReport.visit.startDate.toDate()).format("LLL");
  }, [selectedReport?.visit?.startDate]);

  // Callback for setting selected report
  const handleSetSelectedReport = useCallback((id: string) => {
    setSelectedReport(id);
  }, [setSelectedReport]);

  // Callback for opening FL-324P dialog
  const handleCreateFL324P = useCallback(() => {
    setCreateFL324PDialogOpen(true);
  }, []);

  useEffect(() => {
    if (reportId) {
      handleSetSelectedReport(reportId);
    }
  }, [reportId, handleSetSelectedReport]);

  return {
    selectedReport,
    loading,
    error,
    supervisor,
    selectedCase,
    visitDate,
    isFL324PExportEnabled,
    reportId,
    createFL324PDialogOpen,
    setCreateFL324PDialogOpen,
    handleCreateFL324P,
  };
}; 